import { useEffect, useRef, useState } from "react";
import { Box, HStack, Spinner, Text } from "@chakra-ui/react";
import { toaster } from "@/components/ui/toaster";
import { CampaignService } from "@/services/CampaignService";

// Shared "Połącz z Microsoft" card -- Excel Online equivalent of GoogleSheetsConnectPanel, used by
// StepLeadSource (task-28) and StepOutput (task-29, one instance per Excel output integration). Unlike
// Google Sheets (service account, no login), this needs a real OAuth popup: the user clicks "Połącz z
// Microsoft", a Microsoft consent screen opens in a popup, and MicrosoftGraphController.HandleCallbackAsync
// (task-24) postMessages the result back here (contract: {source:"ms-graph-connect", connectionId,
// connectedAccountEmail, error?}). Once connected, "Sprawdź plik"/"Użyj tego pliku" mirrors
// GoogleSheetsConnectPanel's two-phase check/confirm flow exactly.
const MicrosoftExcelConnectPanel = ({
    campaignId, purpose, fileUrl, worksheetName, connectionId, connectedAccountEmail,
    isConnected, onCheckAccess, onConnected, onAccountConnected,
}) => {
    const [isConnecting, setIsConnecting] = useState(false);
    const [isChecking, setIsChecking] = useState(false);
    const [checkError, setCheckError] = useState(null);
    const [pendingColumns, setPendingColumns] = useState(null);
    const popupRef = useRef(null);

    // Editing the file/worksheet invalidates any previous check -- it was only ever verified for the old value.
    useEffect(() => {
        setPendingColumns(null);
        setCheckError(null);
    }, [fileUrl, worksheetName, connectionId]);

    useEffect(() => {
        // Every mounted panel (one per output integration, plus the lead-source one) listens on the same
        // window -- filter by `purpose` so a callback only updates the panel whose "Połącz z Microsoft"
        // actually opened it, not every panel on the page (message events aren't scoped to the opener).
        const handleMessage = (event) => {
            if (event.data?.source !== "ms-graph-connect" || event.data.purpose !== purpose) return;
            setIsConnecting(false);
            if (event.data.error) {
                toaster.create({ type: "error", title: "Nie udało się połączyć z Microsoft", description: event.data.error });
                return;
            }
            onAccountConnected?.({ connectionId: event.data.connectionId, connectedAccountEmail: event.data.connectedAccountEmail });
            toaster.create({ type: "success", title: "Połączono z Microsoft" });
        };
        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [purpose, onAccountConnected]);

    const handleConnect = async () => {
        setIsConnecting(true);
        try {
            const data = await CampaignService.getMicrosoftGraphAuthorizeUrl(campaignId ?? "", purpose);
            popupRef.current = window.open(data?.url, "ms-graph-connect", "width=600,height=700");
            if (!popupRef.current) {
                setIsConnecting(false);
                toaster.create({ type: "error", title: "Przeglądarka zablokowała okno logowania Microsoft — zezwól na wyskakujące okna i spróbuj ponownie." });
            }
        } catch {
            setIsConnecting(false);
            toaster.create({ type: "error", title: "Nie udało się rozpocząć logowania Microsoft" });
        }
    };

    const handleCheckAccess = async () => {
        if (!fileUrl?.trim() || !worksheetName?.trim()) {
            setCheckError("Podaj link do pliku i nazwę arkusza.");
            return;
        }
        setIsChecking(true);
        setCheckError(null);
        setPendingColumns(null);
        try {
            const result = await onCheckAccess(fileUrl, worksheetName, connectionId);
            if (result?.isConnected) {
                setPendingColumns(result.columns ?? []);
            } else {
                setCheckError(result?.error ?? "Nie udało się zweryfikować dostępu.");
            }
        } finally {
            setIsChecking(false);
        }
    };

    const handleUseThisFile = () => {
        if (!pendingColumns) return;
        onConnected?.(pendingColumns);
        setPendingColumns(null);
        toaster.create({ type: "success", title: "Plik podłączony — zapisz kampanię, żeby to zachować" });
    };

    return (
        <Box borderWidth={1} borderColor="border.primary" borderRadius={8} p={4}>
            <Text fontSize="sm" color="text.white" fontWeight={600}>Połącz z Microsoft</Text>
            <Text fontSize="xs" color="text.grey" mt={0.5}>
                Zaloguj się kontem Microsoft (OneDrive lub SharePoint), żeby aplikacja mogła odczytać i zapisywać plik Excel.
            </Text>
            <HStack mt={2} gap={2} align="center" flexWrap="wrap" justify="space-between">
                <Text fontSize="xs" color={connectionId ? "green.400" : "text.grey"}>
                    Konto: {connectionId ? (connectedAccountEmail ?? "połączono") : "nie połączono"}
                </Text>
                <Box
                    as="button" type="button" onClick={handleConnect} disabled={isConnecting}
                    px={3} py={1.5} borderRadius={6} fontSize="xs"
                    bg="bg.highlight" color="text.white" border="1px solid" borderColor="border.secondary"
                    opacity={isConnecting ? 0.6 : 1}
                    display="flex" alignItems="center" gap={2}
                >
                    {isConnecting && <Spinner size="xs" />}
                    {connectionId ? "Połącz inne konto" : "Połącz z Microsoft"}
                </Box>
            </HStack>

            {connectionId && (
                <>
                    <HStack mt={3} justify="space-between" align="center" gap={4} flexWrap="wrap">
                        <Text fontSize="xs" color={isConnected ? "green.400" : "text.grey"}>
                            Status pliku: {isConnected ? "Połączono" : "Nie połączono"}
                        </Text>
                        <HStack gap={2} flexShrink={0}>
                            <Box
                                as="button" type="button" onClick={handleCheckAccess} disabled={isChecking}
                                px={4} py={2} borderRadius={8} fontSize="sm"
                                bg="bg.highlight" color="text.white" border="1px solid" borderColor="border.secondary"
                                opacity={isChecking ? 0.6 : 1}
                                display="flex" alignItems="center" gap={2}
                            >
                                {isChecking && <Spinner size="xs" />}
                                Sprawdź plik
                            </Box>
                            {pendingColumns && (
                                <Box
                                    as="button" type="button" onClick={handleUseThisFile}
                                    px={4} py={2} borderRadius={8} fontSize="sm"
                                    bg="purple.500" color="white"
                                >
                                    Użyj tego pliku
                                </Box>
                            )}
                        </HStack>
                    </HStack>
                    {pendingColumns && (
                        <Text fontSize="xs" color="green.400" mt={2}>
                            Dostęp działa, odczytano {pendingColumns.length} kolumn. Kliknij „Użyj tego pliku”, żeby zastosować.
                        </Text>
                    )}
                    {checkError && (
                        <Text fontSize="xs" color="red.400" mt={2}>{checkError}</Text>
                    )}
                </>
            )}
        </Box>
    );
};

export default MicrosoftExcelConnectPanel;
